import type { AppContext } from '@application/context/AppContext';
import {
  HybridBackupConfigService,
  subscribeHybridSetupState,
  type HybridSetupSnapshot,
  type HybridSetupState,
} from '@application/backup/HybridBackupConfigService';
import {
  HybridVaultService,
  readHybridVaultStatus,
} from '@application/backup/HybridVaultService';

export interface HybridBackupStatusBannerOptions {
  ctx: AppContext;
  tenantId: string;
  host: HTMLElement;
  onConfigure: () => void;
  onConfirmKey: () => void;
}

function formatLastSentAt(iso: string | null): string {
  if (!iso) return 'Aucun envoi effectué pour le moment.';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Aucun envoi effectué pour le moment.';
  return `Dernier envoi : ${d.toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  })}.`;
}

function bannerClass(variant: 'default' | 'warn' | 'danger'): string {
  if (variant === 'warn') return 'banner warn';
  if (variant === 'danger') return 'banner danger';
  return 'banner';
}

function makeButton(label: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  btn.addEventListener('click', onClick);
  return btn;
}

/**
 * Bannière d’état de la sauvegarde chiffrée Hybride.
 * Masquée si l’offre n’est pas Hybride. Retourne une fonction de nettoyage.
 */
export function mountHybridBackupStatusBanner(opts: HybridBackupStatusBannerOptions): () => void {
  const { ctx, tenantId, host, onConfigure, onConfirmKey } = opts;
  let disposed = false;
  let retryBusy = false;

  if (!HybridVaultService.isEnabled(ctx)) {
    host.replaceChildren();
    host.hidden = true;
    return () => {
      disposed = true;
      host.replaceChildren();
    };
  }

  host.hidden = false;

  const paint = (snapshot: HybridSetupSnapshot): void => {
    if (disposed) return;

    const state: HybridSetupState = snapshot.state;
    const vault = readHybridVaultStatus(tenantId);
    const legacyPlain = HybridVaultService.hasLegacyPlainBackups(tenantId);

    const root = document.createElement('div');
    root.setAttribute('role', 'status');

    const message = document.createElement('div');
    message.style.flex = '1';
    message.style.minWidth = '0';

    let variant: 'default' | 'warn' | 'danger' = 'default';
    let cta: HTMLButtonElement | null = null;

    switch (state) {
      case 'setup_required': {
        variant = 'warn';
        if (legacyPlain) {
          message.textContent =
            'D’anciennes copies non chiffrées existent encore sur le serveur. Configurez la sauvegarde chiffrée maintenant.';
          cta = makeButton('Configurer maintenant', onConfigure);
        } else {
          message.textContent =
            'Configurez la sauvegarde chiffrée pour protéger vos données avant tout envoi.';
          cta = makeButton('Configurer', onConfigure);
        }
        break;
      }
      case 'recovery_key_unconfirmed': {
        variant = 'warn';
        message.textContent =
          'La clé de récupération n’est pas encore confirmée. Les envois de sauvegarde chiffrée sont bloqués.';
        cta = makeButton('Reprendre la confirmation', onConfirmKey);
        break;
      }
      case 'unavailable': {
        variant = 'danger';
        message.textContent =
          'Le coffre sécurisé de ce poste est indisponible. La sauvegarde chiffrée est impossible.';
        break;
      }
      case 'ready': {
        if (vault.lastError) {
          variant = 'warn';
          const last = vault.lastSentAt
            ? ` ${formatLastSentAt(vault.lastSentAt)}`
            : '';
          message.textContent = `Échec du dernier envoi : ${vault.lastError}.${last}`;
          cta = makeButton('Réessayer', () => {
            if (retryBusy || disposed) return;
            retryBusy = true;
            cta!.disabled = true;
            void HybridVaultService.sendNow(ctx, tenantId)
              .then(async () => {
                await HybridBackupConfigService.announceState();
                if (!disposed) {
                  const next = await HybridBackupConfigService.getSetupSnapshot();
                  paint(next);
                }
              })
              .finally(() => {
                retryBusy = false;
              });
          });
        } else {
          variant = 'default';
          message.textContent = formatLastSentAt(vault.lastSentAt);
        }
        break;
      }
      default: {
        const _exhaustive: never = state;
        void _exhaustive;
        return;
      }
    }

    root.className = bannerClass(variant);
    root.appendChild(message);
    if (cta) root.appendChild(cta);
    host.replaceChildren(root);
  };

  const unsubscribe = subscribeHybridSetupState((snapshot) => {
    paint(snapshot);
  });

  void HybridBackupConfigService.getSetupSnapshot().then((snapshot) => {
    paint(snapshot);
  });
  void HybridBackupConfigService.announceState();

  return () => {
    disposed = true;
    unsubscribe();
    host.replaceChildren();
  };
}
